import { importsApi } from '@/api/imports';
import { sitesApi } from '@/api/sites';
import { queryKeys } from '@/lib/queryKeys';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';
import type { EnqueueResult, ImportSiteRequest, SiteOverview } from '@/types/api';

/**
 * Starts importing an existing WordPress (P7). Imported from a watched site,
 * the watched record first gives its names up to the site that will run them.
 */
export function useStartImport(
  callbacks?: MutationCallbacks<
    EnqueueResult & { siteId: string },
    { input: ImportSiteRequest; key: string; from?: SiteOverview | undefined }
  >,
) {
  return useApiMutation(
    async ({ input, key, from }) => {
      if (from && from.engine === 'external' && from.primaryDomain) await sitesApi.unobserve(from.id, from.primaryDomain);
      return importsApi.start(input, key);
    },
    () => [queryKeys.sites.all, queryKeys.jobs.all],
    callbacks,
  );
}

/** The command for the old server. */
export function useIssueImportToken(
  siteId: string,
  callbacks?: MutationCallbacks<{ command: string; scriptUrl: string; expiresAt: string }, void>,
) {
  return useApiMutation(() => importsApi.issueToken(siteId), () => [], callbacks);
}

/** Pointing the names at the imported site, or back at the old server. */
export function useSwitchImportNames(
  siteId: string,
  callbacks?: MutationCallbacks<EnqueueResult, { direction: 'switch' | 'rollback'; confirmation: string; key: string }>,
) {
  return useApiMutation(
    ({ direction, confirmation, key }) =>
      direction === 'switch'
        ? importsApi.switchNames(siteId, confirmation, key)
        : importsApi.rollback(siteId, confirmation, key),
    () => [queryKeys.site(siteId), queryKeys.jobs.all],
    callbacks,
  );
}
